/**
 * TurboSync Pro API Service
 * 
 * Handles all communication with n8n workflows
 * All requests are authenticated with Clerk JWT tokens
 */

import { buildDeliverPlaylistPayload } from '../utils/playlist';
import { planFromServer } from '../constants/plans';

const N8N_BASE = import.meta.env.VITE_N8N_BASE_URL;
const lrmx_request_mode = window.location.hostname === 'localhost' ? 'development' : 'production';
export const AUTH_WAIT_TIMEOUT_MS = 15000;

class TurboSyncAPI {
  constructor() {
    this.authHook = null;
    this.authWaiters = new Set();
    this.designsInFlight = Promise.resolve();
  }

  /**
   * Set the authentication hook (called from AppContext)
   * This gives us access to Clerk's getToken method
   */
  setAuthHook(authHook) {
    this.authHook = authHook;
    const waiters = [...this.authWaiters];
    this.authWaiters.clear();
    waiters.forEach((resolve) => resolve());
  }

  // AppContext installs the hook only once Clerk reports a signed-in session,
  // so a component that calls on mount can run before it; wait instead of failing.
  waitForAuthHook(timeoutMs = AUTH_WAIT_TIMEOUT_MS) {
    if (this.authHook) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const waiter = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        this.authWaiters.delete(waiter);
        reject(new Error('Auth not initialized. Please sign in.'));
      }, timeoutMs);
      this.authWaiters.add(waiter);
    });
  }

  /**
   * Get Clerk JWT token for authentication
   */
  async getClerkToken() {
    await this.waitForAuthHook();

    
    try {
      return await this.authHook.getToken({template: 'turbosync-session'});
    } catch (error) {
      console.error('Failed to get Clerk token:', error);
      throw new Error('Authentication failed. Please sign in again.');
    }
  }

  /**
   * Generic n8n webhook caller with JWT authentication and error handling
   * 
   * @param {string} endpoint - The webhook endpoint path
   * @param {object} payload - Request payload
   * @param {string} method - HTTP method (POST, GET, etc.)
   * @returns {Promise<object>} Response data
   */
  async callN8N(endpoint, payload = {}, method = 'POST', { timeoutMs } = {}) {
    try {
      const token = await this.getClerkToken();

      const controller = timeoutMs ? new AbortController() : null;
      const timerId = controller
        ? setTimeout(() => controller.abort(), timeoutMs)
        : null;

      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        ...(controller ? { signal: controller.signal } : {})
      };

      if (method !== 'GET' && payload) {
        options.body = JSON.stringify({ lrmx_request_mode, ...payload });
      }

      let response;
      try {
        response = await fetch(`${N8N_BASE}${endpoint}`, options);
      } finally {
        if (timerId) clearTimeout(timerId);
      }

      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === 'canva_reauth_required') {
          throw Object.assign(new Error('Your Canva connection has expired. Please reconnect.'), { status: 401, code: 'canva_reauth_required' });
        }
        throw Object.assign(new Error('Session expired. Please sign in again.'), { status: 401 });
      }

      // Handle not found errors
      if (response.status === 404) {
        const err = new Error('Resource not found.');
        err.status = 404;
        throw err;
      }

      // Handle validation errors
      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({ message: 'Invalid request' }));
        
        console.log('===>>>errorData', errorData);
        const err = new Error(errorData.message || errorData.error || 'Invalid authentication request:');
        if (errorData.error_code || errorData.code) {
          err.code = errorData.error_code || errorData.code;
        }
        throw err;
      }

      // Handle conflict errors (e.g., agent already linked)
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({ message: 'Conflict' }));
        throw new Error(errorData.message || errorData.error || 'Resource conflict');
      }

      // Handle server errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorStr = JSON.stringify(errorData);

        if (errorStr.includes('oauth_token_retrieval_error') || errorStr.includes('Token lineage has been revoked') || errorStr.includes('revoked_access_token')) {
          const err = new Error('Your Canva connection has expired. Please reconnect.');
          err.code = 'canva_reauth_required';
          throw err;
        }

        const message = errorData.message
          || (typeof errorData.error === 'string' ? errorData.error : null)
          || 'Something went wrong. Please try again.';
        const code = errorData.code || (typeof errorData.error === 'string' ? errorData.error : undefined);
        throw Object.assign(new Error(message), { status: response.status, ...(code ? { code } : {}) });
      }

      const rawText = await response.text();
      if (!rawText.trim()) {
        console.error('n8n returned an empty response body:', { status: response.status, url: `${N8N_BASE}${endpoint}` });
        throw Object.assign(new Error('The server returned an empty response. Please try again.'), {
          status: response.status,
          code: 'empty_response',
        });
      }
      try {
        return JSON.parse(rawText);
      } catch {
        const { status, statusText } = response;
        const bodyDetail = rawText && rawText.trim()
          ? rawText.slice(0, 500) + (rawText.length > 500 ? '…' : '')
          : '<empty response body>';
        console.error('n8n returned non-JSON response:', bodyDetail, { status, statusText, url: `${N8N_BASE}${endpoint}` });
        throw new Error('Something went wrong processing the request. Please try again.');
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  /**
   * Get Ably token for real-time messaging
   * Token is valid for 1 hour and scoped to user's agent channels
   * 
   * @returns {Promise<string>} Ably token
   */
  async getAblyToken() {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_GET_ABLY_TOKEN || '/webhook/turbosync-saas/get-ably-token',
      {}
    );
    
    if (!result || !result.token) {
      console.error('Invalid Ably token response:', result);
      throw new Error('Failed to get Ably token: Invalid response from server');
    }
    
    return result.token;
  }

  /**
   * Fetch ONE Canva page of DESIGNS for a folder (designs-only since v4).
   *
   * Server-paginated via continuation tokens — first/prev/next traversal, no
   * arbitrary page jump. Default scope is the user's root folder ('root').
   *
   * Folders are fetched separately via {@link fetchFolders} so they render in
   * their own section above the grid, independent of design pagination.
   *
   * @param {object} opts
   * @param {string} [opts.folderId='root'] Canva folder id; 'root' = top level
   * @param {string} [opts.continuation=''] Token from a prior response (omit for first page)
   * @param {number} [opts.limit=25]
   * @param {boolean} [opts.forceRefresh=false] When true, skip the canva_page_cache read (TTL 30 min) and re-fetch live from Canva; the fresh page is then upserted back into the cache. Used by the Refresh button + Sync-Now follow-up. Mount/Next/Prev/folder-enter leave it false to take the fast path.
   * @returns {Promise<{designs: Array, folders: Array, continuation: string|null, hasMore: boolean, folderId: string}>}
   */
  async fetchDesigns({ folderId = 'root', continuation = '', limit = 25, forceRefresh = false } = {}) {
    const request = this.callN8N(
      import.meta.env.VITE_N8N_FETCH_DESIGNS || '/webhook/turbosync-saas/fetch-designs',
      { folderId, continuation, limit, forceRefresh },
      'POST',
      { timeoutMs: 15000 }
    );
    this.designsInFlight = request.then(() => undefined, () => undefined);
    const result = await request;
    return {
      designs: result.designs || [],
      folders: result.folders || [],
      continuation: result.continuation || null,
      hasMore: !!result.hasMore,
      folderId: result.folderId || folderId,
    };
  }

  /**
   * Fetch ONE page of subfolders for a Canva folder context.
   *
   * Capped per request (default 50, max 100) — for users with many folders the
   * caller uses the returned `continuation` token to lazy-load additional
   * batches via "Show more folders". No server-side cache; AppContext caches
   * per-folderId in memory with a 30-min TTL.
   *
   * @param {object} opts
   * @param {string} [opts.folderId='root']
   * @param {string} [opts.continuation='']
   * @param {number} [opts.limit=50]
   * @returns {Promise<{folders: Array, continuation: string|null, hasMore: boolean, folderId: string}>}
   */
  async fetchFolders({ folderId = 'root', continuation = '', limit = 50 } = {}) {
    await this.designsInFlight;
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_FETCH_FOLDERS || '/webhook/turbosync-saas/fetch-folders',
      { folderId, continuation, limit },
      'POST',
      { timeoutMs: 15000 }
    );
    return {
      folders: result.folders || [],
      continuation: result.continuation || null,
      hasMore: !!result.hasMore,
      folderId: result.folderId || folderId,
    };
  }

  /**
   * Tell the backend the caller's Canva link was removed, so the crons stop
   * sweeping them (users.canva_connected=false). Best-effort: it never rejects,
   * because the Clerk disconnect already happened and is what the UI trusts.
   *
   * @returns {Promise<boolean>} whether the backend acknowledged the write
   */
  async markCanvaDisconnected() {
    try {
      const result = await this.callN8N(
        import.meta.env.VITE_N8N_CANVA_DISCONNECTED || '/webhook/turbosync-saas/canva-disconnected',
        {},
        'POST',
        { timeoutMs: 10000 }
      );
      return result?.success === true;
    } catch (error) {
      console.warn('Could not record the Canva disconnect:', error.message);
      return false;
    }
  }

  /**
   * Fetch user's linked desktop agents with online status
   * 
   * @returns {Promise<{agents: Array, plan: object|null}>} Linked agents and the org's plan (null when not reported)
   */
  async fetchLinkedAgents() {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_FETCH_AGENTS || '/webhook/turbosync-saas/fetch-agents'
    );
    return { agents: result.agents || [], plan: planFromServer(result) };
  }

  /**
   * Link a desktop agent to the user's account
   * 
   * @param {string} agentId - UUID of the desktop agent
   * @param {string} description - Optional description for the agent
   * @returns {Promise<object>} Linked agent object
   */
  async linkAgent(agentId, description = 'My Agent') {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_LINK_AGENT || '/webhook/turbosync-saas/link-agent',
      {
        agent_id: agentId,
        description
      }
    );
    return result.agent;
  }

  /**
   * Unlink a desktop agent from the user's account
   * 
   * @param {string} agentId - UUID of the desktop agent
   * @returns {Promise<object>} Success response
   */
  async unlinkAgent(agentId) {
    const baseEndpoint = import.meta.env.VITE_N8N_UNLINK_AGENT || '/webhook/unlink-agent-c8b13ed7-b79d-4315-b08d-37bb4399ece7';
    const endpointWithParams = `${baseEndpoint}?agent_id=${encodeURIComponent(agentId)}`;
    
    return this.callN8N(
      endpointWithParams,
      {},
      'DELETE'
    );
  }

  /**
   * Send design assets to desktop agent
   * Downloads and caches design assets on the agent
   * 
   * @param {string} designId - Canva design ID
   * @param {string} designTitle - Design title/name
   * @param {string} agentId - Desktop agent UUID
   * @returns {Promise<object>} Success response
   */
  async sendDesignToAgent(designId, designTitle, agentId) {
    return this.callN8N(
      import.meta.env.VITE_N8N_SEND_DESIGN_TO_AGENT || '/webhook/f682479a-f27b-43d4-9495-91e02e275792/send-designs-to-agent',
      {
        design_id: designId,
        design_title: designTitle,
        agent_id: agentId,
      },
      'POST',
      // The webhook holds the connection open through an asset-download poll
      // loop (~9×3s). Without a ceiling, a dropped hold leaves the fetch
      // pending forever and the design card frozen mid-dispatch. Set well
      // above the max legitimate hold so a slow-but-live run is never aborted.
      { timeoutMs: 60000 }
    );
  }

  /**
   * Load design in ProPresenter via desktop agent
   * Triggers ProPresenter automation to create presentation
   * 
   * @param {string} designId - Canva design ID
   * @param {string} designTitle - Design title/name
   * @param {string} agentId - Desktop agent UUID
   * @returns {Promise<object>} Success response
   */
  async loadInProPresenter(designId, designTitle, agentId) {
    return this.callN8N(
      import.meta.env.VITE_N8N_LOAD_IN_PROPRESENTER || '/webhook/turbosync-saas/load-in-propresenter',
      {
        design_id: designId,
        design_title: designTitle,
        agent_id: agentId,
      },
      'POST',
      // Holds the connection open through the ProPresenter-readiness poll loop
      // (~9×5s). Same rationale as sendDesignToAgent — bound it so a dropped
      // hold surfaces as a Retry instead of a permanently-frozen card, but keep
      // the ceiling above the longest real poll window.
      { timeoutMs: 90000 }
    );
  }

  async checkDesignStatus(designId, designTitle, agentId) {
    return this.callN8N(
      import.meta.env.VITE_N8N_CHECK_DESIGN_STATUS || '/webhook/xy82479a-f27b-99d4-9495-91e02e272254/fetch-agent-design-status',
      {
        design_id: designId,
        design_title: designTitle,
        agent_id: agentId
      }
    );
  }

  /**
   * @deprecated Use sendDesignToAgent or loadInProPresenter instead
   */
  async sendToProPresenter(designId, designTitle, agentId) {
    return this.loadInProPresenter(designId, designTitle, agentId);
  }

  /**
   * Fetch the calling user's auto-dispatch configuration + Canva auth health
   *
   * @returns {Promise<{
   *   enabled: boolean,
   *   schedule: object|null,
   *   lastRunAt: string|null,
   *   canvaAuthFailure: {at: string, reason: string|null}|null,
   *   plan: object|null,
   * }>}
   */
  async fetchUserAutoDispatch() {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_FETCH_USER_AUTO_DISPATCH || '/webhook/turbosync-saas/fetch-user-auto-dispatch',
      {},
      'GET'
    );
    return {
      enabled: result?.auto_dispatch_enabled ?? false,
      schedule: result?.auto_dispatch_schedule ?? null,
      lastRunAt: result?.last_auto_dispatch_at ?? null,
      canvaAuthFailure: result?.canva_auth_failure_at
        ? { at: result.canva_auth_failure_at, reason: result.canva_auth_failure_reason ?? null }
        : null,
      plan: planFromServer(result),
    };
  }

  /**
   * Persist the calling user's auto-dispatch schedule
   *
   * @param {{enabled: boolean, schedule: object|null}} config
   * @returns {Promise<object>}
   */
  async setAutoDispatchSchedule(config) {
    return this.callN8N(
      import.meta.env.VITE_N8N_SET_AUTO_DISPATCH_SCHEDULE || '/webhook/turbosync-saas/set-auto-dispatch-schedule',
      { enabled: config.enabled, schedule: config.schedule }
    );
  }

  /**
   * Run the detect-then-dispatch loop immediately for the calling user
   *
   * @returns {Promise<object>}
   */
  async triggerSyncNow() {
    return this.callN8N(
      import.meta.env.VITE_N8N_TRIGGER_SYNC_NOW || '/webhook/turbosync-saas/trigger-sync-now',
      {}
    );
  }

  /**
   * Clear is_new / is_updated flags for the given designs
   *
   * @param {string[]} designIds
   * @returns {Promise<object>}
   */
  async markDesignsSeen(designIds) {
    return this.callN8N(
      import.meta.env.VITE_N8N_MARK_DESIGNS_SEEN || '/webhook/turbosync-saas/mark-designs-seen',
      { design_ids: designIds }
    );
  }

  async setAutoSyncDesign(designId, enrolled) {
    return this.callN8N(
      import.meta.env.VITE_N8N_SET_AUTO_SYNC_DESIGN || '/webhook/turbosync-saas/set-auto-sync-design',
      { design_id: designId, enrolled }
    );
  }

  /**
   * Fetch per-Media Station settings (ProPresenter library) for the caller's org
   *
   * A 404 means the Station Settings workflow is not active yet; callers
   * check `error.status === 404` and treat it as "not configured".
   *
   * @returns {Promise<Array<{agent_id: string, description: string, library_name: string|null, settings_updated_at: string|null}>>}
   */
  async fetchStationSettings() {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_FETCH_STATION_SETTINGS || '/webhook/turbosync-saas/fetch-station-settings',
      {},
      'GET'
    );
    return result?.stations || [];
  }

  /**
   * Persist settings for one Media Station and push them to it over Ably
   *
   * The webhook saves first, then waits up to ~10 s for the station's ack, so
   * `delivered: false` means "saved, station offline or on an older build".
   *
   * @param {string} agentId - Media Station UUID
   * @param {{library_name: string|null}} settings - Only the keys being set; null clears
   * @returns {Promise<{success: boolean, agent_id: string, settings: object, settings_updated_at: string, correlation_id: string, delivered: boolean, station_state: object|null}>}
   */
  async setStationSettings(agentId, settings) {
    return this.callN8N(
      import.meta.env.VITE_N8N_SET_STATION_SETTINGS || '/webhook/turbosync-saas/set-station-settings',
      { agent_id: agentId, settings },
      'POST',
      { timeoutMs: 20000 }
    );
  }

  /**
   * Ask a ProPresenter 22 Media Station to open its queued imports now (the
   * operator's "Import now"). The station still never starts ProPresenter
   * unless its "Allow start" setting is on. A 404 means the relay webhook is
   * not active yet, and the caller points the operator at the station instead.
   *
   * @param {string} agentId - Media Station UUID
   * @returns {Promise<{success: boolean, delivered: boolean}>}
   */
  async requestImportNow(agentId) {
    return this.callN8N(
      import.meta.env.VITE_N8N_IMPORT_NOW || '/webhook/turbosync-saas/import-now',
      { agent_id: agentId },
      'POST',
      { timeoutMs: 15000 }
    );
  }

  /**
   * Second leg of a playlist delivery: ask the Media Station to combine the
   * already-sent designs, in order, into one ProPresenter presentation.
   *
   * Every failure is an Error with `status` and `code`: `invalid_playlist`
   * (400), `unauthorized` (401), `agent_not_found` (404), `not_available`
   * (a 404/500 without the contract's `error`, i.e. the webhook is not
   * active), `station_failure` (502, with `stationDetails.missingItems`),
   * `upstream_error` (other 502s, network errors) or `station_timeout`
   * (504, or the 90 s client timeout).
   *
   * @param {string} agentId - Media Station UUID
   * @param {object} playlist - The `playlist` slice (id, name, ordered items)
   * @returns {Promise<{success: boolean, playlist_id: string, correlation_id: string, delivered: boolean, station_status: string, pro_file_path: string|null, slide_count: number|null, group_count: number|null}>}
   */
  async deliverPlaylist(agentId, playlist) {
    const endpoint = import.meta.env.VITE_N8N_DELIVER_PLAYLIST || '/webhook/turbosync-saas/deliver-playlist';
    const fail = (status, code, message, extra = {}) =>
      Object.assign(new Error(message), { status, code, ...extra });

    const token = await this.getClerkToken();
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), 90000);

    let response;
    try {
      response = await fetch(`${N8N_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(buildDeliverPlaylistPayload(playlist, agentId, lrmx_request_mode)),
        signal: controller.signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') throw fail(504, 'station_timeout', 'Request timed out.');
      throw fail(0, 'upstream_error', 'Network error. Please check your connection.');
    } finally {
      clearTimeout(timerId);
    }

    const body = await response.json().catch(() => ({}));
    if (response.ok) return body;

    const { status } = response;
    const code = typeof body?.error === 'string' ? body.error : null;
    const detail = body?.detail || body?.message || '';
    if (status === 401) throw fail(status, 'unauthorized', 'Session expired. Please sign in again.');
    if (status === 400) throw fail(status, 'invalid_playlist', detail || 'Invalid playlist');
    if (status === 404 && code === 'agent_not_found') throw fail(status, code, 'Media Station not found');
    if ((status === 404 || status === 500) && !code) throw fail(status, 'not_available', detail || 'Webhook not registered');
    if (status === 504) throw fail(status, 'station_timeout', 'The Media Station did not respond in time');
    if (code === 'station_failure') {
      const stationDetails = body.station_details || {};
      throw fail(status, code, stationDetails.error || 'The Media Station could not build the presentation', {
        stationDetails: { ...stationDetails, missingItems: stationDetails.missingItems || [] },
      });
    }
    throw fail(status, 'upstream_error', detail || 'Something went wrong. Please try again.');
  }

  /**
   * Saved playlists (contract D): one webhook, `op` picks list/save/delete.
   *
   * Every failure is an Error with `status` and `code`: `not_configured`
   * (a 404/500 without the contract's `error`, i.e. the workflow is not
   * active), `playlist_not_found` (404), `invalid_op`/`invalid_playlist`
   * (400), `unauthorized` (401), `upstream_error` (502, network errors) or
   * `timeout` (the 15 s client timeout).
   */
  async callPlaylists(body) {
    const endpoint = import.meta.env.VITE_N8N_PLAYLISTS || '/webhook/turbosync-saas/playlists';
    const fail = (status, code, message) => Object.assign(new Error(message), { status, code });

    const token = await this.getClerkToken();
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), 15000);

    let response;
    try {
      response = await fetch(`${N8N_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ lrmx_request_mode, ...body }),
        signal: controller.signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') throw fail(0, 'timeout', 'Request timed out. Please try again.');
      throw fail(0, 'upstream_error', 'Network error. Please check your connection.');
    } finally {
      clearTimeout(timerId);
    }

    const data = await response.json().catch(() => ({}));
    if (response.ok) return data;

    const { status } = response;
    const code = typeof data?.error === 'string' ? data.error : null;
    const detail = data?.detail || data?.message || '';
    if (status === 401) throw fail(status, 'unauthorized', 'Session expired. Please sign in again.');
    if ((status === 404 || status === 500) && !code) throw fail(status, 'not_configured', detail || 'Webhook not registered');
    if (status === 404) throw fail(status, code, detail || 'Playlist not found');
    if (status === 400) throw fail(status, code || 'invalid_playlist', detail || 'Invalid playlist');
    throw fail(status, 'upstream_error', detail || 'Something went wrong. Please try again.');
  }

  /** @returns {Promise<Array<{id, name, service_date, status, target_agent_id, last_delivered_at, updated_at, items}>>} */
  async fetchPlaylists() {
    const result = await this.callPlaylists({ op: 'list' });
    return result?.playlists || [];
  }

  /**
   * Replace semantics: send the whole playlist. Omit `id` to create one.
   *
   * @param {{id?, name, service_date?, target_agent_id?, status?, last_delivered_at?, items: Array<{position, design_id, title, thumbnail_url, page_count}>}} playlist
   * @returns {Promise<object>} The saved playlist row, with its `id` and `updated_at`
   */
  async savePlaylist(playlist) {
    const result = await this.callPlaylists({ op: 'save', playlist });
    return result?.playlist;
  }

  async deletePlaylist(playlistId) {
    return this.callPlaylists({ op: 'delete', playlist_id: playlistId });
  }

  /**
   * Update user settings
   *
   * @param {object} settings - Settings object (key-value pairs)
   * @returns {Promise<object>} Updated settings
   */
  async updateSettings(settings) {
    return this.callN8N(
      import.meta.env.VITE_N8N_UPDATE_SETTINGS || '/webhook/turbosync-saas/update-settings',
      {
        settings
      }
    );
  }

  /**
   * Fetch user settings
   * 
   * @returns {Promise<object>} Settings object
   */
  async fetchSettings() {
    const result = await this.callN8N(
      import.meta.env.VITE_N8N_FETCH_SETTINGS || '/webhook/turbosync-saas/fetch-settings',
      {},
      'GET'
    );
    return result.settings || {};
  }

  /**
   * Logout helper
   * Clears local session data
   */
  logout() {
    // Clear any local storage if needed
    sessionStorage.clear();
    localStorage.clear();
    // Redirect handled by Clerk
  }
}

// Export singleton instance
export default new TurboSyncAPI();



